import { Link } from 'react-router-dom';
import { Shield, Lock, CheckCircle, ChevronDown, Phone, Mail } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { ROUTE_PATHS, WHATSAPP_NUMBER, CONTACT_EMAIL, CONTACT_PHONE } from '@/lib/index';
import { PROPERTIES, SERVICES } from '@/data/index';
import { IMAGES } from '@/assets/images';
import { PropertyCard, ServiceCard } from '@/components/Cards';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Building2: () => <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center text-accent text-2xl">🏢</div>,
  MapPin: () => <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center text-accent text-2xl">📍</div>,
  Home: () => <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center text-accent text-2xl">🏠</div>,
  Ruler: () => <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center text-accent text-2xl">📐</div>,
  FileText: () => <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center text-accent text-2xl">📄</div>,
  TrendingUp: () => <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center text-accent text-2xl">📈</div>,
};

export default function Home() {
  const featuredProperties = PROPERTIES.slice(0, 3);

  return (
    <Layout>
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.HERO_MAIN}
            alt="Zanzibar Luxury Real Estate"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/80 via-primary/60 to-primary/90" />
        </div>

        <div className="relative z-10 container mx-auto px-4 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-7xl lg:text-8xl font-serif font-bold text-accent mb-6 tracking-tight"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            ANCHOR VAULT ESTATE
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-2xl md:text-3xl text-white mb-4 font-light"
          >
            Anchored in Trust. Built for Legacy.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-lg md:text-xl text-white/90 mb-12 max-w-3xl mx-auto"
          >
            Discover luxury real estate in Paje, Zanzibar, Tanzania. From beachfront plots to exclusive villas,
            we deliver secure investments with our revolutionary Vault System™.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Button
              asChild
              size="lg"
              className="bg-accent hover:bg-accent/90 text-primary font-semibold px-8 py-6 text-lg"
            >
              <Link to={ROUTE_PATHS.PROPERTIES}>Explore Properties</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-accent text-accent hover:bg-accent/10 px-8 py-6 text-lg"
            >
              <Link to={ROUTE_PATHS.INVESTMENT}>Our Vault System</Link>
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="absolute bottom-12 left-1/2 -translate-x-1/2"
          >
            <a href="#services" className="text-white/70 hover:text-white transition-colors">
              <ChevronDown className="w-8 h-8 animate-bounce" />
            </a>
          </motion.div>
        </div>
      </section>

      <section id="services" className="py-24 bg-primary">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-accent mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Our Services
            </h2>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              Comprehensive real estate solutions tailored to your investment goals in Zanzibar.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {SERVICES.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              return (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <ServiceCard
                    icon={IconComponent ? <IconComponent /> : null}
                    title={service.title}
                    description={service.description}
                  />
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="properties" className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-primary mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Featured Properties
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Explore our handpicked selection of premium properties in Zanzibar's most sought-after locations.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {featuredProperties.map((property, index) => (
              <motion.div
                key={property.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <PropertyCard property={property} />
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <Button
              asChild
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8"
            >
              <Link to={ROUTE_PATHS.PROPERTIES}>View All Properties</Link>
            </Button>
          </div>
        </div>
      </section>

      <section id="investment" className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.INVEST_BG}
            alt="Investment Background"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary via-primary/95 to-primary" />
        </div>

        <div className="relative z-10 container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-accent mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              The Vault System™
            </h2>
            <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto mb-8">
              Your investment is protected by our revolutionary escrow-based guaranteed payment system.
              Every dollar is secured in a bank vault until your property is delivered exactly as promised.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-center"
            >
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-accent/20 flex items-center justify-center">
                <Shield className="w-10 h-10 text-accent" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">100% Security</h3>
              <p className="text-white/80">
                Your funds are protected by bank-level escrow security throughout the entire process.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center"
            >
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-accent/20 flex items-center justify-center">
                <Lock className="w-10 h-10 text-accent" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Locked in The Vault</h3>
              <p className="text-white/80">
                Money stays locked in escrow until all legal and construction requirements are verified.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-center"
            >
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-accent/20 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-accent" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Guaranteed Delivery</h3>
              <p className="text-white/80">
                Funds are released only when your property is completed and legally transferred to you.
              </p>
            </motion.div>
          </div>

          <div className="text-center">
            <Button
              asChild
              size="lg"
              className="bg-accent hover:bg-accent/90 text-primary font-semibold px-8"
            >
              <Link to={ROUTE_PATHS.INVESTMENT}>Learn More About The Vault System</Link>
            </Button>
          </div>
        </div>
      </section>

      <section id="contact" className="py-24 bg-primary">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-accent mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Ready to Invest?
            </h2>
            <p className="text-lg text-white/90 mb-8">
              Contact us today to discuss your real estate investment in Zanzibar.
              Our team is ready to guide you through every step of the process.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8">
              <a
                href={`tel:${CONTACT_PHONE}`}
                className="flex items-center gap-2 text-white hover:text-accent transition-colors"
              >
                <Phone className="w-5 h-5" />
                <span className="font-mono">{CONTACT_PHONE}</span>
              </a>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="flex items-center gap-2 text-white hover:text-accent transition-colors"
              >
                <Mail className="w-5 h-5" />
                <span>{CONTACT_EMAIL}</span>
              </a>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-[#25D366] hover:bg-[#20BA5A] text-white font-semibold px-8"
              >
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                >
                  <FaWhatsapp className="w-5 h-5" />
                  WhatsApp Us
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-accent text-accent hover:bg-accent/10 px-8"
              >
                <Link to={ROUTE_PATHS.CONTACT}>Contact Form</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}